import * as THREE from 'three';
import { type SimAudioBinding, type Surface, audio, bindSimAudio, lootSfx, themeForZone, uiSound } from '../audio';
import { BATTLE_ITEM_SKILLS } from '../content/battleItems';
import { getZone, hasZone, registerContent } from '../content';
import { npcDef } from '../content/npcs';
import { FixedStep } from '../core/fixedStep';
import type { V2 } from '../core/math';
import { type GameAction, Input, type InputListener } from '../input/Input';
import { AssetLibrary } from '../render/assets';
import { CameraRig } from '../render/CameraRig';
import {
  AutoQuality,
  type DeviceInfo,
  clearGraphicsProfile,
  guessQuality,
  isSoftwareRenderer,
  loadGraphicsProfile,
  lowerQuality,
  readDeviceInfo,
  saveGraphicsProfile,
} from '../render/autoQuality';
import { GameRenderer } from '../render/GameRenderer';
import type { Quality } from '../render/RenderContext';
import type { Actor } from '../sim/actor';
import type { SimEvent } from '../sim/events';
import { createPlayer, resolvePlayerSkills } from '../sim/factory';
import { registry } from '../sim/registry';
import { resolveSkill } from '../sim/skills';
import { cleanse } from '../sim/statusSystem';
import { playerNetId } from '../sim/ids';
import { type ActionKey, GRADE_RANK } from '../sim/types';
import { skillIcon } from '../ui/icons';
import { Hud, type HudState, type ItemSlotView } from '../ui/Hud';
import type { MinimapIcon } from '../ui/Minimap';
import type { LabelInfo } from '../ui/WorldLabels';
import type { GameApi } from '../ui/app/api';
import { setApi } from '../ui/app/api';
import { mountUi } from '../ui/app/App';
import { type DialogueState, type PanelId, bump, closePanel, openPanel, ui } from '../ui/app/store';
import type { AccountSave } from './account';
import { accountBonus } from './bonuses';
import { newCharacter } from './character';
import type { NetHooks, ZoneOpts } from './netHooks';
import { npcDialogue, npcName } from './interactions';
import { itemDef } from './items';
import { rollLoot } from './loot';
import { Progression } from './Progression';
import { accept, allQuests, npcMarker, objectiveMarkers, questDef, setNpcNameResolver, trackerLines } from './quests';
import { SaveStore, browserStorage, exportSave, importSave } from './save';
import { Showcase } from './Showcase';
import { ZoneRuntime } from './ZoneRuntime';

export const SIM_DT = 1 / 60;
const QUALITY_LABEL: Record<Quality, string> = { low: 'Low', medium: 'Medium', high: 'High' };

const ACTION_KEYS: Partial<Record<GameAction, ActionKey>> = {
  skillQ: 'Q', skillW: 'W', skillE: 'E', skillR: 'R', skillA: 'A', skillS: 'S', skillD: 'D', skillF: 'F',
  dash: 'SPACE', identityZ: 'Z', identityX: 'X', awakening: 'V', basic: 'BASIC',
};

const PANEL_KEYS: Partial<Record<GameAction, PanelId>> = {
  inventory: 'inventory', character: 'character', skills: 'skills', map: 'map', quests: 'quests', sigils: 'sigils',
  cards: 'cards', journal: 'journal', emotes: 'emotes', party: 'party', bounties: 'bounty', bonds: 'bonds', haven: 'haven', market: 'market',
};

type Opts = ZoneOpts;

/**
 * Top-level orchestrator: renderer, input, UI, saves, progression and the
 * currently loaded zone instance.
 */
export class Game implements InputListener, GameApi {
  readonly renderer: GameRenderer;
  readonly rig: CameraRig;
  readonly input: Input;
  readonly assets = new AssetLibrary();
  readonly hud: Hud;
  readonly store = new SaveStore();
  account: AccountSave;
  progression: Progression | null = null;
  zone: ZoneRuntime | null = null;
  player: Actor | null = null;
  /** This client's peer id ('local' offline; the transport's id online). */
  localPeer = 'local';
  /** Multiplayer session (null while offline). */
  net: NetHooks | null = null;
  private fixed = new FixedStep(SIM_DT, 6);
  /** Longest frame (s) the simulation catches up on; slower frames slow the game down instead of jumping it ahead. */
  private maxFrameDt = 0.1;
  private last = performance.now();
  private hitStopT = 0;
  private moveRepath = 0;
  private attackRepeat = 0;
  private cursor: V2 = { x: 0, z: 0 };
  private followPoint = new THREE.Vector3();
  private autosaveT = 0;
  private portalCooldown = 0;
  timeScale = 1;
  private slowT = 0;
  loading = false;
  onFrame: ((dt: number) => void) | null = null;
  /** Instance modules (rift, colossus, siege…) attach zone scripts here. */
  readonly zoneSetups: ((game: Game, z: ZoneRuntime, opts: Opts) => void)[] = [];
  /** Handlers for world interactables by kind (life skills, haven stations…). */
  readonly interactHandlers = new Map<string, (game: Game, id: string) => boolean>();
  /** Extra per-frame systems (day cycle, networking…). */
  readonly systems: ((game: Game, dt: number) => void)[] = [];
  /** Key actions owned by feature modules (mount…), called on press. */
  readonly actionHandlers = new Map<GameAction, (game: Game) => void>();
  instanceOpts: Opts = {};
  returnPoint: { zone: string; spawn: string } | null = null;
  /** Drives the simulation while the tab is hidden (browsers pause animation frames). */
  private bgTicker: Worker | null = null;
  /** "Auto" graphics quality (null while the player has picked a level). */
  private autoQuality: AutoQuality | null = null;
  private deviceInfo: DeviceInfo | null = null;
  /** Per-device preferences that must not travel with an exported account. */
  private readonly prefs = browserStorage();
  private lastRenderAt = 0;
  private softwareNoticeShown = false;
  private readonly sfx: SimAudioBinding;
  /** 3D stage behind the title / select / create screens. */
  private showcase: Showcase | null = null;
  private showing = false;
  private combatCheckT = 0;

  constructor(readonly root: HTMLElement) {
    registerContent();
    for (const s of BATTLE_ITEM_SKILLS) registry.registerSkill(s);
    setNpcNameResolver(npcName);
    this.account = this.store.load();
    this.rig = new CameraRig();
    this.renderer = new GameRenderer(root, new THREE.Scene(), this.rig.camera);
    this.input = new Input(this.renderer.renderer.domElement);
    this.input.setListener(this);
    this.hud = new Hud(root);
    this.hud.root.classList.add('hidden');
    setApi(this);
    mountUi(root);
    this.setupMenuBar();
    this.hud.onTrackerClick = () => openPanel('quests');
    this.sfx = this.bindAudio();
    this.applySettings();
    window.addEventListener('beforeunload', () => this.save());
    document.addEventListener('visibilitychange', () => (document.hidden ? this.startBackgroundTicks() : this.stopBackgroundTicks()));
  }

  /** The local player's actor id (identical on every peer). */
  get playerId(): number {
    return playerNetId(this.localPeer);
  }

  async init(): Promise<void> {
    audio.attachUnlock();
    audio.setTheme('title');
    ui.screen.value = 'boot';
    ui.loadingText.value = 'Gathering shards…';
    await this.assets.init();
    this.renderer.renderer.setAnimationLoop(() => this.frame());
    ui.screen.value = 'title';
  }

  private setupMenuBar(): void {
    const b = (label: string, action: GameAction, panel: PanelId) => this.hud.addMenuButton(label, () => this.input.bindingLabel(action), () => openPanel(panel));
    b('Character', 'character', 'character');
    b('Bag', 'inventory', 'inventory');
    b('Skills', 'skills', 'skills');
    b('Quests', 'quests', 'quests');
    b('Map', 'map', 'map');
    b('Sigils', 'sigils', 'sigils');
    b('Cards', 'cards', 'cards');
    b('Journal', 'journal', 'journal');
    b('Social', 'party', 'party');
    b('Settings', 'settings', 'settings');
  }

  /** Sim events → positional sound effects; UI clicks → interface sounds. */
  private bindAudio(): SimAudioBinding {
    const actor = (id: number) => this.zone?.world.actors.get(id);
    document.addEventListener('click', (e) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest('button, .btn, .tab')) uiSound(audio, 'click', 0.6);
    });
    return bindSimAudio(audio, {
      pos: (id) => actor(id)?.pos ?? null,
      isLocalPlayer: (id) => id === this.player?.id,
      isMonster: (id) => actor(id)?.kind === 'monster',
      isBoss: (id) => {
        const m = actor(id)?.monster;
        return !!m && GRADE_RANK[m.grade] >= GRADE_RANK.named;
      },
      creature: (id) => {
        const m = actor(id)?.monster;
        return m ? registry.monsters.get(m.defId)?.creature : undefined;
      },
      skillElement: (sid) => registry.skills.get(sid)?.element,
      skillKind: (sid) => registry.skills.get(sid)?.kind,
      listener: () => this.player?.pos ?? null,
      listenerYaw: () => this.rig.yaw,
      surfaceAt: (x, z) => {
        const L = this.zone?.layout;
        if (!L) return undefined;
        const w = L.surfaceAt(x, z);
        let best = 0;
        for (let i = 1; i < 4; i++) if ((w[i] ?? 0) > (w[best] ?? 0)) best = i;
        return SURFACE_OF[L.ground[best] ?? 'grass'] ?? 'grass';
      },
    });
  }

  /** Combat music layer: on while an elite or stronger monster is fighting nearby. */
  private updateCombatMusic(dt: number): void {
    this.combatCheckT -= dt;
    if (this.combatCheckT > 0) return;
    this.combatCheckT = 0.5;
    const z = this.zone;
    const p = this.player;
    if (!z || !p) return;
    let fighting = false;
    for (const a of z.world.aliveActors) {
      const m = a.monster;
      if (!m || GRADE_RANK[m.grade] < GRADE_RANK.elite || m.idle > 6) continue;
      if (Math.hypot(a.pos.x - p.pos.x, a.pos.z - p.pos.z) < 35) {
        fighting = true;
        break;
      }
    }
    audio.setCombat(fighting);
  }

  applySettings(): void {
    const s = this.account.settings;
    audio.applySettings({ master: s.master, music: s.music, sfx: s.sfx });
    this.applyQuality(s.quality);
    this.rig.shakeScale = s.shake;
    this.renderer.flashReduction = s.flashReduction;
    this.input.swapMouse = s.swapMouse;
    this.hud.damage.enabled = s.showDamage;
    document.documentElement.style.setProperty('--hud-scale', String(s.hudScale));
    if (Object.keys(s.bindings).length) this.input.setBindings(s.bindings);
    this.hud.refreshMenuKeys();
  }

  /** The graphics level: the player's pick, or what Auto chose for this device. */
  private applyQuality(q: AccountSave['settings']['quality']): void {
    if (q !== 'auto') {
      this.autoQuality = null;
      this.setGraphicsLevel(q);
      return;
    }
    if (!this.autoQuality) {
      const d = this.device();
      const learned = loadGraphicsProfile(this.prefs, d.gpu);
      this.autoQuality = new AutoQuality(learned ?? { level: guessQuality(d), ceiling: 'high' }, performance.now());
    }
    this.setGraphicsLevel(this.autoQuality.level);
  }

  private setGraphicsLevel(q: Quality): void {
    if (this.renderer.quality !== q) this.renderer.setQuality(q);
    ui.graphicsLevel.value = q;
  }

  private device(): DeviceInfo {
    return (this.deviceInfo ??= readDeviceInfo(this.renderer.renderer.getContext()));
  }

  /** Feeds Auto graphics quality one rendered frame; only frames of play count. */
  private adaptQuality(now: number): void {
    const ms = this.lastRenderAt > 0 ? now - this.lastRenderAt : 16.7;
    this.lastRenderAt = now;
    const auto = this.autoQuality;
    if (!auto || !this.zone || this.loading || ui.screen.value !== 'game') return;
    if (!this.softwareNoticeShown && isSoftwareRenderer(this.device())) {
      this.softwareNoticeShown = true;
      this.notify('The browser is drawing the game without the graphics card. Turn on hardware acceleration in its settings for a smooth game.', '#e5a54d');
    }
    const next = auto.frame(ms, now, this.renderer.resolutionState());
    if (!next) return;
    const lowered = next === lowerQuality(this.renderer.quality);
    this.setGraphicsLevel(next);
    saveGraphicsProfile(this.prefs, this.device().gpu, auto.state);
    if (lowered) this.notify(`Graphics lowered to ${QUALITY_LABEL[next]} to keep the game smooth (Settings → Video).`, '#e5a54d');
    bump();
  }

  // ================================================================= GameApi

  get zoneId(): string | null {
    return this.zone?.layout.id ?? null;
  }

  get zoneKind(): string | null {
    return this.zone?.layout.kind ?? null;
  }

  createCharacter(name: string, classId: string): string {
    const id = `ch_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e6).toString(36)}`;
    const c = newCharacter(id, name, classId);
    this.account.characters.push(c);
    this.account.lastCharacterId = id;
    this.save();
    bump();
    return id;
  }

  deleteCharacter(id: string): void {
    this.account.characters = this.account.characters.filter((c) => c.id !== id);
    if (this.account.lastCharacterId === id) this.account.lastCharacterId = this.account.characters[0]?.id ?? null;
    this.save();
    bump();
  }

  async playCharacter(id: string): Promise<void> {
    const c = this.account.characters.find((x) => x.id === id);
    if (!c) return;
    this.account.lastCharacterId = id;
    const p = new Progression(this.account, c);
    this.progression = p;
    this.bindProgression(p);
    ui.screen.value = 'loading';
    await this.net?.ready();
    await this.enterZone(hasZone(c.zone) ? c.zone : 'emberwild', c.spawn || 'start');
    ui.screen.value = 'game';
    this.hud.root.classList.remove('hidden');
    for (const q of this.nextAutoQuests()) if (p.acceptQuest(q)) break;
    this.net?.onCharacter();
    bump();
  }

  exportSave(): string {
    this.save();
    return exportSave(this.account);
  }

  importSave(code: string): void {
    this.account = importSave(code);
    this.save();
    bump();
  }

  logout(): void {
    this.save();
    this.net?.onLogout();
    this.zone?.dispose();
    this.zone = null;
    this.player = null;
    this.progression = null;
    this.hud.root.classList.add('hidden');
    closePanel();
    ui.dialogue.value = null;
    ui.deathOptions.value = null;
    ui.screen.value = 'select';
    audio.setTheme('title');
  }

  save(): void {
    const prog = this.progression;
    if (prog && this.zone) {
      const k = this.zone.layout.kind;
      if (k === 'field' || k === 'town' || k === 'sea' || k === 'island' || k === 'haven') {
        prog.char.zone = this.zone.layout.id;
      } else if (this.returnPoint) {
        prog.char.zone = this.returnPoint.zone;
        prog.char.spawn = this.returnPoint.spawn;
      }
    }
    this.store.save(this.account);
  }

  setSetting<K extends keyof AccountSave['settings']>(key: K, value: AccountSave['settings'][K]): void {
    // Choosing Auto again re-detects the device instead of trusting what it learned before.
    if (key === 'quality' && value === 'auto' && this.account.settings.quality !== 'auto') {
      clearGraphicsProfile(this.prefs);
      this.autoQuality = null;
    }
    this.account.settings[key] = value;
    this.applySettings();
    this.store.saveSoon(this.account);
    bump();
  }

  notify(text: string, color?: string): void {
    this.hud.toast(text, color);
  }

  // ================================================================= zones

  async enterZone(zoneId: string, spawn = 'start', opts: Opts = {}): Promise<void> {
    const prog = this.progression;
    if (!prog) throw new Error('No character loaded');
    this.loading = true;
    ui.loadingProgress.value = 0.1;
    const layout = getZone(zoneId);
    ui.loadingText.value = layout.name;
    const old = this.zone;
    this.zone = null;
    this.player = null;
    old?.dispose();
    ui.dialogue.value = null;
    ui.deathOptions.value = null;
    this.renderer.grade.desaturate = 0;

    // Group instances are hosted by the party leader / matchmaking organiser.
    const host = typeof opts.host === 'string' && opts.host ? opts.host : this.localPeer;
    const z = new ZoneRuntime({
      layout,
      assets: this.assets,
      localPeer: this.localPeer,
      hostPeer: host,
      partySize: Math.max(1, Math.min(8, Number(opts.size) || 1)),
      pvp: layout.kind === 'arena',
      seed: `${zoneId}:${Date.now()}`,
    });
    // Gated portals (boss exits, next raid gates) look sealed until their flag is set.
    z.portalOpen = (portal) => !portal.requires || !!prog.char.flags[portal.requires];
    z.bindFeedback(
      (a) => this.rig.addTrauma(a),
      (c, p, d) => this.renderer.flash(c, p, d),
      (s) => (this.hitStopT = Math.max(this.hitStopT, s)),
    );
    ui.loadingProgress.value = 0.3;
    await z.buildScene();
    ui.loadingProgress.value = 0.7;
    await this.assets.preload([registry.cls(prog.char.classId).model]);
    ui.loadingProgress.value = 0.9;
    z.spawnNpcs();
    // Collected interactables stay collected.
    const col = this.account.collectibles;
    for (const it of [...z.interactables]) {
      const gone = (it.kind === 'seed' && col.seeds.includes(it.id)) || (it.kind === 'heart' && col.hearts.includes(it.id)) || (it.kind === 'vista' && col.vistas.includes(it.id)) || (it.kind === 'chest' && !!prog.char.flags[`chest_${it.id}`]);
      if (gone) z.removeInteractable(it.id);
    }
    z.spawnPacks();

    const sp = layout.spawnPoints[spawn] ?? layout.spawnPoints.start ?? { x: 0, z: 0, facing: 0 };
    this.instanceOpts = opts;
    this.spawnPlayer(z, { x: sp.x, z: sp.z }, sp.facing ?? 0);
    prog.resetInstanceUses();
    if (layout.kind === 'field' || layout.kind === 'town') {
      prog.char.zone = zoneId;
      prog.char.spawn = spawn;
      this.returnPoint = null;
    }
    for (const setup of this.zoneSetups) setup(this, z, opts);
    for (const s of z.scripts) s.start?.(z);

    this.renderer.setScene(z.scene);
    this.zone = z;
    audio.setTheme(themeForZone(zoneId, layout.kind));
    this.sfx.reset();
    this.rig.snapTo(new THREE.Vector3(sp.x, layout.heightAt(sp.x, sp.z), sp.z));
    this.hud.minimap.setZone(layout, z.nav);
    this.hud.zoneKind = layout.kind;
    this.hud.damage.clear();
    this.hud.labels.clear();
    this.hud.zoneBanner(layout.name, layout.subtitle);
    this.portalCooldown = 2;
    this.loading = false;
    this.autoQuality?.settle(performance.now());
    ui.loadingProgress.value = 1;
    prog.questEvent({ type: 'reach', zone: zoneId, pos: { x: sp.x, z: sp.z } });
    this.save();
  }

  /** Creates (or recreates) the local player's actor from the progression build. */
  spawnPlayer(z: ZoneRuntime, pos: V2, facing: number, hpPct = 1): Actor {
    const prog = this.progression as Progression;
    const c = prog.char;
    const built = prog.build({ pvp: z.layout.kind === 'arena' });
    const id = this.playerId;
    if (z.world.actors.has(id)) z.world.removeActor(id);
    const open = z.layout.kind === 'field' || z.layout.kind === 'town';
    const team = this.instanceOpts.team;
    const p = createPlayer(z.world, {
      id,
      owner: this.localPeer,
      team: team === 'red' || team === 'blue' ? team : 'players',
      name: c.name,
      classId: c.classId,
      level: c.level,
      pos,
      base: built.base,
      bar: c.bar,
      awakening: c.awakening,
      skillLevels: c.skillLevels,
      tripods: c.tripods,
      passives: built.passives,
      skillMods: built.skillMods,
      awakeningUses: open ? 99 : built.awakeningUses,
    });
    for (const s of BATTLE_ITEM_SKILLS) p.skills.set(s.id, resolveSkill(s, 10, []));
    p.facing = facing;
    p.hp = Math.max(1, Math.round(p.stats.maxHp * hpPct));
    z.localPlayerId = p.id;
    this.player = p;
    return p;
  }

  refreshPlayer(): void {
    const prog = this.progression;
    const p = this.player;
    const pl = p?.player;
    if (!prog || !p || !pl) return;
    const built = prog.build({ pvp: this.zone?.layout.kind === 'arena' });
    pl.level = prog.char.level;
    pl.bar = [...prog.char.bar];
    pl.awakening = prog.char.awakening;
    pl.skillLevels = { ...prog.char.skillLevels };
    pl.tripods = structuredClone(prog.char.tripods);
    pl.skillMods = built.skillMods;
    p.passives = built.passives;
    p.setBase(built.base);
    resolvePlayerSkills(p);
    for (const s of BATTLE_ITEM_SKILLS) p.skills.set(s.id, resolveSkill(s, 10, []));
    bump();
  }

  travel(zone: string, spawn: string): void {
    if (this.loading) return;
    closePanel();
    ui.dialogue.value = null;
    ui.screen.value = 'loading';
    void this.enterZone(zone, spawn).then(() => {
      ui.screen.value = 'game';
    });
  }

  enterInstance(id: string, opts: Opts = {}): void {
    if (this.loading || !this.zone) return;
    if (!hasZone(id)) {
      this.notify('That place is not reachable yet.', '#e5484d');
      return;
    }
    if (this.net?.interceptInstance(id, opts)) return;
    const cur = this.zone.layout;
    if (cur.kind === 'field' || cur.kind === 'town') this.returnPoint = { zone: cur.id, spawn: this.nearestReturnSpawn() };
    closePanel();
    ui.dialogue.value = null;
    ui.screen.value = 'loading';
    void this.enterZone(id, 'start', opts).then(() => {
      ui.screen.value = 'game';
    });
  }

  /** Reloads the current field/town at the nearest spawn (channel switch). */
  reloadZone(): void {
    const z = this.zone;
    if (!z) return;
    this.travel(z.layout.id, this.nearestReturnSpawn());
  }

  private nearestReturnSpawn(): string {
    const z = this.zone;
    const p = this.player;
    if (!z || !p) return 'start';
    let best = 'start';
    let bestD = Infinity;
    for (const [name, sp] of Object.entries(z.layout.spawnPoints)) {
      // Only arrival points: never hazard, wave or boss markers.
      if (name !== 'start' && !name.startsWith('from')) continue;
      const d = Math.hypot(sp.x - p.pos.x, sp.z - p.pos.z);
      if (d < bestD) {
        bestD = d;
        best = name;
      }
    }
    return best;
  }

  leaveInstance(): void {
    this.net?.leftInstance();
    const back = this.returnPoint ?? { zone: 'havenreach', spawn: 'start' };
    this.returnPoint = null;
    this.travel(hasZone(back.zone) ? back.zone : 'emberwild', back.spawn);
  }

  // ================================================================= progression wiring

  private bindProgression(p: Progression): void {
    p.on('levelUp', (e) => {
      this.hud.announce(`Level ${e.level}!`, 'success');
      this.refreshPlayer();
      if (this.player) this.player.hp = this.player.stats.maxHp;
      const pos = this.player?.pos;
      if (pos && this.zone) this.zone.vfx.handle({ type: 'areaErupt', id: -1, origin: pos, facing: 0, shape: { kind: 'circle', radius: 2 }, vfx: 'pillar', hue: 0xffd070 });
      this.store.saveSoon(this.account);
    });
    p.on('item', (e) => {
      this.hud.toast(`<span style="color:var(--r-${e.rarity})">${e.name}</span> ×${e.count}`, `var(--r-${e.rarity})`);
      audio.play(lootSfx(e.rarity), { volume: 0.7 });
    });
    p.on('equipDrop', (e) => {
      this.hud.toast(`<span style="color:var(--r-${e.item.rarity})">${e.item.name}</span>`, `var(--r-${e.item.rarity})`);
      audio.play(lootSfx(e.item.rarity));
    });
    p.on('questAccepted', (e) => {
      this.hud.toast(`Quest accepted: ${e.quest.name}`, '#e49955');
      bump();
    });
    p.on('questStep', (e) => {
      if (e.hint) this.hud.announce(e.hint, 'info');
      bump();
    });
    p.on('questReady', (e) => {
      this.hud.toast(`Objective complete: ${questDef(e.quest).name}`, '#7bd66b');
      bump();
    });
    p.on('questCompleted', (e) => {
      this.hud.announce(`Quest complete — ${e.quest.name}`, 'success');
      this.store.saveSoon(this.account);
      for (const q of this.nextAutoQuests()) if (accept(p.char, q)) this.hud.toast(`Quest accepted: ${questDef(q).name}`, '#e49955');
      bump();
    });
    p.on('questUpdated', () => bump());
    p.on('wallet', () => bump());
    p.on('statsChanged', () => this.refreshPlayer());
    p.on('toast', (e) => this.hud.toast(e.text, e.color));
    p.on('legacyRank', (e) => this.hud.toast(`Legacy Rank ${e.rank}`, '#b080ff'));
    p.on('collectible', () => bump());
    p.on('lifeSkill', (e) => this.hud.toast(`${e.skill} reached level ${e.level}`, '#9be07a'));
  }

  /** Auto-accepted story quests whose prerequisites are met. */
  private nextAutoQuests(): string[] {
    const p = this.progression;
    if (!p) return [];
    const out: string[] = [];
    for (const q of allQuests()) {
      if (!q.auto || p.char.quests.done.includes(q.id) || p.char.quests.active[q.id]) continue;
      if ((q.requires ?? []).every((r) => p.char.quests.done.includes(r)) && (q.requiresLevel ?? 0) <= p.char.level) out.push(q.id);
    }
    return out;
  }

  // ================================================================= input

  onAction(action: GameAction, pressed: boolean, repeat: boolean): void {
    const p = this.player;
    const z = this.zone;
    if (ui.screen.value !== 'game' || !p || !z || this.loading) return;
    if (pressed && action === 'settings') {
      if (ui.dialogue.value) this.closeDialogue();
      else if (ui.panel.value) closePanel();
      else openPanel('settings');
      return;
    }
    if (pressed && !repeat) {
      const panel = PANEL_KEYS[action];
      if (panel) {
        openPanel(panel);
        return;
      }
      if (action === 'interact') {
        if (!ui.dialogue.value) this.interact();
        return;
      }
      const handler = this.actionHandlers.get(action);
      if (handler) {
        handler(this);
        return;
      }
      if (action === 'chat') {
        ui.chatOpen.value = true;
        return;
      }
      if (action === 'hideUi') {
        this.hud.root.classList.toggle('hidden');
        return;
      }
      if (action === 'item1' || action === 'item2' || action === 'item3' || action === 'item4') {
        this.useBattleItem(Number(action.slice(4)) - 1);
        return;
      }
    }
    if (ui.dialogue.value) return;
    const key = ACTION_KEYS[action];
    if (key) {
      if (repeat) return;
      if (pressed) z.world.pressKey(p, key, { ...this.cursor });
      else z.world.releaseKey(p, key);
    }
  }

  onPointer(button: 'move' | 'attack', pressed: boolean): void {
    const p = this.player;
    const z = this.zone;
    if (!p || !z || ui.dialogue.value || this.loading || ui.screen.value !== 'game') return;
    if (button === 'move' && pressed) {
      this.updateCursor();
      z.world.moveTo(p, { ...this.cursor });
      this.moveRepath = 0.12;
    }
    if (button === 'attack' && pressed) {
      this.updateCursor();
      z.world.pressKey(p, 'BASIC', { ...this.cursor });
      this.attackRepeat = 0.1;
    }
  }

  onWheel(deltaY: number): void {
    if (ui.panel.value) return;
    this.rig.wheel(deltaY);
  }

  private updateCursor(): void {
    const z = this.zone;
    if (!z) return;
    this.input.groundPoint(this.rig.camera, z.heightAt, this.cursor);
  }

  // ================================================================= interaction

  interact(): void {
    const z = this.zone;
    const p = this.player;
    const prog = this.progression;
    if (!z || !p || !prog || !p.alive) return;
    const npc = z.nearestNpc(p.pos);
    if (npc?.npcId) {
      prog.questEvent({ type: 'talk', target: npc.npcId });
      ui.dialogue.value = npcDialogue(prog.char, npc.npcId, prog.bondStage(npc.npcId));
      z.world.faceTowards(p, npc.pos);
      z.world.stop(p);
      bump();
      return;
    }
    const it = z.nearestInteractable(p.pos);
    if (it) this.useInteractable(it.id);
  }

  useInteractable(id: string): void {
    const z = this.zone;
    const prog = this.progression;
    if (!z || !prog) return;
    const it = z.interactables.find((i) => i.id === id);
    if (!it) return;
    const custom = this.interactHandlers.get(it.kind);
    if (custom?.(this, id)) return;
    switch (it.kind) {
      case 'seed':
        if (prog.collect('seeds', it.id)) this.hud.toast(`Glimmerseed found (${this.account.collectibles.seeds.length})`, '#8dffb0');
        prog.questEvent({ type: 'interact', target: 'seed' });
        z.removeInteractable(it.id);
        break;
      case 'heart':
        if (prog.collect('hearts', it.id)) this.hud.announce(`${String(it.data?.name ?? 'Heart')} claimed! Max HP +1%, +2 skill points`, 'success');
        z.removeInteractable(it.id);
        break;
      case 'vista':
        if (prog.collect('vistas', it.id)) this.hud.announce(String(it.data?.name ?? 'Vista discovered'), 'info');
        this.rig.targetZoom = 1;
        z.removeInteractable(it.id);
        break;
      case 'lore':
        prog.collect('lore', it.id);
        this.hud.toast('Lore recorded in your Explorer’s Journal', '#d0b0ff');
        prog.questEvent({ type: 'interact', target: it.id });
        break;
      case 'chest': {
        prog.char.flags[`chest_${it.id}`] = true;
        prog.grantDrop(rollLoot(String(it.data?.loot ?? 'chest'), { level: z.layout.levelRange[1], rng: prog.rng }), 'Chest');
        z.removeInteractable(it.id);
        break;
      }
      case 'waystone':
        if (!prog.char.waystones.includes(it.id)) {
          prog.char.waystones.push(it.id);
          this.hud.toast(`Waystone attuned: ${String(it.data?.name ?? z.layout.name)}`, '#7ad8ff');
        }
        openPanel('waystone');
        break;
      default:
        prog.questEvent({ type: 'interact', target: it.id });
        if (it.kind === 'questItem') z.removeInteractable(it.id);
    }
    bump();
    this.store.saveSoon(this.account);
  }

  dialogueOption(opt: DialogueState['options'][number]): void {
    const prog = this.progression;
    const d = ui.dialogue.value;
    if (!prog || !d) return;
    switch (opt.kind) {
      case 'bye':
        this.closeDialogue();
        return;
      case 'quest':
        if (opt.id) prog.acceptQuest(opt.id);
        this.closeDialogue();
        break;
      case 'turnin': {
        if (!opt.id || opt.id.startsWith('talk:')) {
          this.closeDialogue();
          return;
        }
        const q = prog.completeQuest(opt.id);
        if (q) ui.rewards.value = { title: q.name, subtitle: 'Quest complete', silver: q.rewards.silver ?? 0, gold: q.rewards.gold ?? 0, items: q.rewards.items ?? [], equip: [], cards: q.rewards.card ? [q.rewards.card] : [], xp: q.rewards.xp };
        ui.dialogue.value = npcDialogue(prog.char, d.npcId, prog.bondStage(d.npcId));
        break;
      }
      case 'service': {
        const map: Record<string, PanelId> = { shop: 'shop', forge: 'temper', bounty: 'bounty', market: 'market', arena: 'instances', dungeon: 'instances', bonds: 'bonds', haven: 'haven', sail: 'sail', cards: 'cards', sigils: 'sigils', gems: 'inventory', storage: 'inventory', travel: 'waystone' };
        const panel = opt.id === 'gems' ? 'sigils' : map[opt.id ?? ''];
        this.closeDialogue();
        if (panel) openPanel(panel, opt.id === 'arena' ? 'arena' : opt.id === 'gems' ? 'gems' : d.npcId);
        break;
      }
      case 'bond': {
        const key = `bondDay_${d.npcId}`;
        if (prog.char.flags[key] === this.account.day) {
          ui.dialogue.value = { ...d, lines: [`${npcDef(d.npcId).name} smiles. "Come back tomorrow."`], index: 0 };
          return;
        }
        const gained = 30 + Math.floor(Math.random() * 20);
        prog.char.flags[key] = this.account.day;
        prog.addBond(d.npcId, gained);
        ui.dialogue.value = { ...d, lines: [`You share a song together. Bond +${gained}.`], index: 0 };
        break;
      }
    }
    bump();
  }

  closeDialogue(): void {
    ui.dialogue.value = null;
  }

  useBattleItem(slot: number): void {
    const prog = this.progression;
    const p = this.player;
    const z = this.zone;
    if (!prog || !p || !z || !p.alive) return;
    const id = prog.char.battleSlots[slot];
    if (!id) return;
    const def = itemDef(id);
    if (!def.battle) return;
    const skill = p.skills.get(def.battle.skill);
    if (!skill || (p.cooldowns.get(skill.id) ?? 0) > 0) return;
    const limited = z.layout.kind !== 'field' && z.layout.kind !== 'town';
    if (!prog.consumeBattleItem(id, limited)) {
      this.hud.toast(limited ? 'No uses left in this instance' : `Out of ${def.name}`, '#e5484d');
      return;
    }
    if (z.world.useSkill(p, skill, `I${slot + 1}` as ActionKey, { ...this.cursor })) {
      p.cooldowns.set(skill.id, def.battle.cooldown);
      if (id === 'potion_panacea') cleanse(z.world, p);
      // Haven research makes healing potions stronger.
      const extra = def.battle.category === 'potion' ? accountBonus('potionHeal') : 0;
      let healPct = 0;
      for (const a of skill.stages[0]?.actions ?? []) if (a.kind === 'hit' && a.hit.healPct) healPct = a.hit.healPct;
      if (extra > 0 && healPct > 0) z.world.heal(p, p.stats.maxHp * healPct * extra, p.id);
    }
    bump();
  }

  emote(id: string): void {
    this.hud.toast(`You ${id}.`);
    this.net?.emote(id);
  }

  sendChat(text: string, channel: string): void {
    if (this.net) this.net.chat(text, channel);
    else this.notify('You are playing offline.');
  }

  restDay(): void {
    /* replaced by the day-cycle system */
  }

  // ================================================================= simulation feedback

  private handleEvents(events: SimEvent[]): void {
    const z = this.zone;
    const me = this.player;
    const prog = this.progression;
    if (!z || !me || !prog) return;
    for (const ev of events) {
      switch (ev.type) {
        case 'hit': {
          const mine = ev.srcId === me.id;
          const onMe = ev.tgtId === me.id;
          if (!mine && !onMe) break;
          const pos = z.actorPos(ev.tgtId, new THREE.Vector3());
          if (!pos) break;
          pos.y += z.actorHeight(ev.tgtId) * 0.85;
          const kind = ev.heal ? 'heal' : ev.shield ? 'shield' : onMe && !mine ? 'taken' : ev.dot ? 'dot' : ev.crit ? 'crit' : ev.staggered ? 'stag' : 'normal';
          if (ev.amount > 0) this.hud.damage.spawn(ev.tgtId, pos, { amount: ev.amount, kind, back: ev.back && mine, head: ev.head && mine, counter: ev.counter && mine }, z.world.time);
          break;
        }
        case 'death': {
          const dead = z.world.actors.get(ev.id);
          if (!dead) break;
          if (dead.id === me.id) {
            this.onPlayerDeath();
            break;
          }
          if (dead.monster) {
            const m = dead.monster;
            const contributed = m.threat.has(me.id) || Math.hypot(dead.pos.x - me.pos.x, dead.pos.z - me.pos.z) < 30;
            if (contributed) {
              const def = registry.monster(m.defId);
              prog.onKill(def, m.level, { tags: m.tags, loot: !m.tags.includes('noLoot') });
              if (GRADE_RANK[def.grade] >= GRADE_RANK.named) {
                this.slowMo(0.35, 0.8);
                this.hud.announce(`${def.name} defeated!`, 'success');
                if (def.grade === 'named') prog.collect('fieldBosses', def.id);
              }
            }
          }
          break;
        }
        case 'counter':
          if (ev.srcId === me.id) this.hud.callout('Counter Attack!');
          break;
        case 'staggerBreak':
          this.hud.announce('Staggered!', 'success');
          break;
        case 'partBreak':
          this.hud.callout(`Destroyed — ${ev.name}`);
          break;
        case 'identity':
          if (ev.actorId === me.id && ev.op === 'perfect') this.hud.perfect();
          break;
        case 'announce':
          this.hud.announce(ev.text, ev.style);
          break;
        default:
          break;
      }
    }
  }

  private onPlayerDeath(): void {
    const prog = this.progression;
    const z = this.zone;
    if (!prog || !z) return;
    const open = z.layout.kind === 'field' || z.layout.kind === 'town' || z.layout.kind === 'dungeon';
    ui.deathOptions.value = { canPlume: open && (prog.char.inventory.stacks.phoenix_plume ?? 0) > 0, canCheckpoint: open, timer: 0 };
    this.renderer.grade.desaturate = 1;
  }

  /** Revive at the zone entrance, or in place with a Phoenix Plume. */
  revive(inPlace: boolean): void {
    const z = this.zone;
    const p = this.player;
    const prog = this.progression;
    if (!z || !p || !prog) return;
    if (inPlace) {
      if (!prog.consumeBattleItem('phoenix_plume', false)) return;
      z.world.revive(p, 0.5);
    } else {
      const sp = z.layout.spawnPoints.start ?? { x: 0, z: 0 };
      p.pos = { x: sp.x, z: sp.z };
      p.prevPos = { ...p.pos };
      z.world.revive(p, 1);
    }
    ui.deathOptions.value = null;
    this.renderer.grade.desaturate = 0;
  }

  /**
   * Lets the simulation catch up on frames up to `seconds` long, so game time keeps pace with the clock
   * on machines that render only a few frames per second (tests on software WebGL).
   */
  setMaxCatchUp(seconds: number): void {
    this.maxFrameDt = seconds;
    this.fixed = new FixedStep(SIM_DT, Math.max(6, Math.ceil(seconds / SIM_DT)));
  }

  slowMo(scale: number, seconds: number): void {
    // Slowing the local simulation would desynchronise it from other players.
    if (this.net?.peerCount) return;
    this.timeScale = scale;
    this.slowT = seconds;
  }

  // ================================================================= frame

  /** Menus render the showcase dais; entering the world switches back to the zone scene. */
  private updateShowcase(dt: number): void {
    const screen = ui.screen.value;
    const menu = screen === 'title' || screen === 'select' || screen === 'create';
    if (!menu) {
      if (this.showing) {
        this.showing = false;
        this.rig.camera.fov = this.rigFov;
        this.rig.camera.updateProjectionMatrix();
        if (this.zone) this.renderer.setScene(this.zone.scene);
      }
      return;
    }
    if (!this.showcase) this.showcase = new Showcase(this.assets);
    if (!this.showing) {
      this.showing = true;
      this.rigFov = this.rig.camera.fov;
      this.renderer.setScene(this.showcase.scene);
    }
    const last = this.account.characters.find((c) => c.id === this.account.lastCharacterId) ?? this.account.characters[0];
    this.showcase.setClass(screen === 'title' ? (last?.classId ?? 'ravager') : ui.showcaseClass.value);
    // Keep the hero in the open space of each screen's layout.
    const offset = screen === 'create' ? 0.45 : 0;
    this.showcase.update(dt, this.rig.camera, offset, screen === 'title');
  }

  private rigFov = 35;

  private startBackgroundTicks(): void {
    if (this.bgTicker || !this.net) return;
    try {
      const src = 'const t = setInterval(() => postMessage(0), 50); onmessage = () => { clearInterval(t); close(); };';
      const w = new Worker(URL.createObjectURL(new Blob([src], { type: 'text/javascript' })));
      w.onmessage = () => this.frame(false);
      this.bgTicker = w;
    } catch {
      /* no workers: the simulation pauses while hidden */
    }
  }

  private stopBackgroundTicks(): void {
    this.bgTicker?.postMessage(0);
    this.bgTicker = null;
    this.last = performance.now();
  }

  /** One frame: simulation steps, then (when visible) presentation. */
  private frame(render = true): void {
    const now = performance.now();
    const dt = Math.min(this.maxFrameDt, (now - this.last) / 1000);
    this.last = now;
    const z = this.zone;
    const prog = this.progression;
    if (z && this.player && prog && !this.loading) {
      this.updateCursor();
      const p = this.player;
      z.world.setAim(p, this.cursor);
      const inputBlocked = !!ui.dialogue.value || ui.screen.value !== 'game';
      if (this.input.moveHeld && !inputBlocked) {
        this.moveRepath -= dt;
        if (this.moveRepath <= 0) {
          this.moveRepath = 0.12;
          z.world.moveTo(p, { ...this.cursor });
        }
      }
      if (this.input.attackHeld && !inputBlocked) {
        this.attackRepeat -= dt;
        if (this.attackRepeat <= 0) {
          this.attackRepeat = 0.1;
          z.world.pressKey(p, 'BASIC', { ...this.cursor });
        }
      }
      if (this.slowT > 0) {
        this.slowT -= dt;
        if (this.slowT <= 0) this.timeScale = 1;
      }
      let simDt = dt * this.timeScale;
      if (this.hitStopT > 0) {
        const stop = Math.min(this.hitStopT, simDt);
        this.hitStopT -= stop;
        simDt -= stop;
      }
      const alpha = this.fixed.advance(simDt, (step) => {
        const events = z.step(step);
        this.handleEvents(events);
        if (render) this.sfx.handle(events);
      });
      this.sfx.frame(dt);
      this.updateCombatMusic(dt);
      if (!render) {
        for (const sys of this.systems) sys(this, dt);
        return;
      }
      z.render(dt * this.timeScale, alpha);
      const v = z.views.get(p.id);
      if (v) this.followPoint.copy(v.root.position);
      else this.followPoint.set(p.pos.x, z.heightAt(p.pos.x, p.pos.z), p.pos.z);
      if (ui.dialogue.value) {
        const npc = [...z.world.actors.values()].find((a) => a.npcId === ui.dialogue.value?.npcId);
        this.rig.setFocus(npc ? new THREE.Vector3((npc.pos.x + p.pos.x) / 2, this.followPoint.y, (npc.pos.z + p.pos.z) / 2) : null, 0.05);
      } else this.rig.setFocus(null);
      this.rig.update(dt, this.followPoint);
      z.lighting.follow(this.followPoint);
      this.checkPortals(dt);
      for (const sys of this.systems) sys(this, dt);
      prog.char.playTime += dt;
      this.account.playTime += dt;
      this.autosaveT += dt;
      if (this.autosaveT > 30) {
        this.autosaveT = 0;
        this.save();
      }
      const hs = this.hudState();
      if (hs) this.hud.update(dt, hs, this.rig.camera);
      this.hud.labelWorld(this.labelInfos(), this.rig.camera);
      this.onFrame?.(dt);
    }
    if (!render) return;
    this.updateShowcase(dt);
    this.adaptQuality(now);
    this.renderer.render(dt);
  }

  private checkPortals(dt: number): void {
    const z = this.zone;
    const p = this.player;
    const prog = this.progression;
    if (!z || !p || !prog || !p.alive) return;
    this.portalCooldown = Math.max(0, this.portalCooldown - dt);
    if (this.portalCooldown > 0) return;
    prog.questEvent({ type: 'reach', zone: z.layout.id, pos: p.pos });
    const portal = z.portalAt(p.pos);
    if (!portal) return;
    this.portalCooldown = 3;
    if (portal.requires && !prog.char.flags[portal.requires]) {
      this.hud.toast('The way is sealed.', '#e5484d');
      return;
    }
    if (portal.minLevel && prog.char.level < portal.minLevel) {
      this.hud.toast(`Requires level ${portal.minLevel}`, '#e5484d');
      return;
    }
    if (!hasZone(portal.to)) {
      this.hud.toast(`${portal.label} is not open yet.`, '#e5484d');
      return;
    }
    if (portal.kind === 'dungeon' || portal.kind === 'instance') this.enterInstance(portal.to, {});
    else if (z.layout.kind !== 'field' && z.layout.kind !== 'town' && z.layout.kind !== 'sea' && z.layout.kind !== 'haven' && z.layout.kind !== 'island') this.leaveInstance();
    else this.travel(portal.to, portal.toSpawn ?? 'start');
  }

  get cursorPoint(): V2 {
    return this.cursor;
  }

  // ================================================================= HUD

  private hudState(): HudState | null {
    const z = this.zone;
    const p = this.player;
    const prog = this.progression;
    if (!z || !p || !prog) return null;
    const c = prog.char;
    const icons: MinimapIcon[] = [];
    for (const a of z.world.aliveActors) {
      if (a === p) continue;
      const dx = a.pos.x - p.pos.x;
      const dz = a.pos.z - p.pos.z;
      if (dx * dx + dz * dz > 70 * 70) continue;
      if (a.kind === 'monster' && a.monster) icons.push({ x: a.pos.x, z: a.pos.z, kind: GRADE_RANK[a.monster.grade] >= GRADE_RANK.named ? 'boss' : GRADE_RANK[a.monster.grade] >= GRADE_RANK.elite ? 'elite' : 'monster' });
      else if (a.kind === 'npc') icons.push({ x: a.pos.x, z: a.pos.z, kind: a.npcId && npcMarker(c, a.npcId) ? 'quest' : 'npc' });
      else if (a.kind === 'player') icons.push({ x: a.pos.x, z: a.pos.z, kind: 'ally' });
    }
    for (const it of z.interactables) {
      if (it.kind === 'waystone') icons.push({ x: it.x, z: it.z, kind: 'waystone' });
      else if (it.kind === 'gather' || it.kind === 'dig' || it.kind === 'fish') icons.push({ x: it.x, z: it.z, kind: 'gather' });
      else if (it.kind === 'chest') icons.push({ x: it.x, z: it.z, kind: 'chest' });
    }
    for (const m of objectiveMarkers(c, z.layout.id)) icons.push({ x: m.x, z: m.z, kind: 'objective' });
    for (const pt of z.layout.portals) icons.push({ x: pt.x, z: pt.z, kind: 'portal' });
    icons.push({ x: p.pos.x, z: p.pos.z, kind: 'player', facing: p.facing });

    let prompt: HudState['prompt'] = null;
    if (!ui.dialogue.value) {
      const npc = z.nearestNpc(p.pos);
      if (npc?.npcId) prompt = { key: 'G', text: `Talk to ${npc.name}` };
      else {
        const it = z.nearestInteractable(p.pos);
        if (it) prompt = { key: 'G', text: interactLabel(it.kind, it.data) };
      }
    }
    const items: (ItemSlotView | null)[] = c.battleSlots.map((id) => {
      if (!id) return null;
      const def = itemDef(id);
      const sk = def.battle ? p.skills.get(def.battle.skill) : undefined;
      return { icon: sk ? skillIcon(sk, def.icon.hue) : '', count: c.inventory.stacks[id] ?? 0, cd: sk ? (p.cooldowns.get(sk.id) ?? 0) : 0, cdMax: def.battle?.cooldown ?? 1, name: def.name };
    });
    return {
      player: p,
      world: z.world,
      cls: registry.cls(c.classId),
      level: c.level,
      xpPct: prog.xpPct(),
      legacyRank: prog.legacyRank,
      zoneName: z.layout.name,
      channel: this.net?.channelLabel() ?? (z.layout.kind === 'field' || z.layout.kind === 'town' ? 'Offline' : 'Instance'),
      currencies: { silver: this.account.wallet.silver, gold: this.account.wallet.gold, shards: c.inventory.stacks.aether_shard ?? 0 },
      tracker: trackerLines(c),
      party: this.net?.partyFrames() ?? [],
      items,
      minimapIcons: icons,
      prompt,
    };
  }

  private labelInfos(): LabelInfo[] {
    const z = this.zone;
    const p = this.player;
    const prog = this.progression;
    if (!z || !p || !prog) return [];
    const out: LabelInfo[] = [];
    const head = new THREE.Vector3();
    for (const a of z.world.aliveActors) {
      if (a === p) continue;
      const dx = a.pos.x - p.pos.x;
      const dz = a.pos.z - p.pos.z;
      if (dx * dx + dz * dz > 40 * 40) continue;
      const v = z.views.get(a.id);
      if (!v) continue;
      v.headWorld(head);
      if (a.kind === 'npc' && a.npcId) {
        const def = npcDef(a.npcId);
        out.push({ actor: a, head: head.clone(), role: def.role, showBar: false, questMark: npcMarker(prog.char, a.npcId) });
      } else if (a.kind === 'monster' && a.monster) {
        const important = GRADE_RANK[a.monster.grade] >= GRADE_RANK.elite;
        if (GRADE_RANK[a.monster.grade] >= GRADE_RANK.named) continue;
        if (!important && a.hp >= a.stats.maxHp) continue;
        out.push({ actor: a, head: head.clone(), showBar: true });
      } else if (a.kind === 'player') {
        out.push({ actor: a, head: head.clone(), showBar: false });
      }
    }
    return out;
  }
}

/** Footstep material per terrain layer. */
const SURFACE_OF: Record<string, Surface> = {
  grass: 'grass', dryGrass: 'grass', moss: 'grass', dirt: 'grass', ash: 'sand', sand: 'sand',
  rock: 'stone', cobble: 'stone', tiles: 'stone', crystal: 'stone', snow: 'snow', ice: 'snow', planks: 'wood',
};

function interactLabel(kind: string, data?: Record<string, string | number | boolean>): string {
  switch (kind) {
    case 'gather':
      return data?.node === 'ore' ? 'Mine ore vein' : data?.node === 'timber' ? 'Chop timber' : 'Forage herbs';
    case 'dig':
      return 'Excavate';
    case 'fish':
      return 'Fish';
    case 'seed':
      return 'Collect Glimmerseed';
    case 'chest':
      return 'Open chest';
    case 'waystone':
      return 'Use Waystone';
    case 'vista':
      return 'Take in the view';
    case 'lore':
      return 'Read';
    case 'heart':
      return 'Claim the Heart';
    case 'station':
      return `Use ${String(data?.station ?? 'station')}`;
    default:
      return 'Interact';
  }
}
